import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Edit2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { slug: "", name: "", description: "", external_url: "", image_url: "", total_topics: "", total_sections: "" };

export default function ModulesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/modules");
      setModules(data);
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing("new"); setForm(emptyForm); };
  const startEdit = (m) => { setEditing(m.slug); setForm({ ...emptyForm, ...m, total_topics: m.total_topics || "", total_sections: m.total_sections || "" }); };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        description: form.description.trim(),
        external_url: form.external_url.trim(),
        image_url: form.image_url.trim() || null,
        total_topics: form.total_topics ? parseInt(form.total_topics, 10) : null,
        total_sections: form.total_sections ? parseInt(form.total_sections, 10) : null,
      };
      if (editing === "new") {
        await api.post("/admin/modules", payload);
      } else {
        await api.put(`/admin/modules/${editing}`, payload);
      }
      close(); load();
    } catch (e) { setError(errorMessage(e)); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete module "${m.name}"? This will also unassign it from all users.`)) return;
    try {
      await api.delete(`/admin/modules/${m.slug}`);
      load();
    } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <AppShell section="admin">
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline mb-2">Admin Console</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">Modules</h1>
          </div>
          {canEdit && (
            <button data-testid="add-module-button" onClick={startCreate} className="h-10 px-4 inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-[13px]">
              <Plus className="h-3.5 w-3.5" /> New module
            </button>
          )}
        </div>

        {error && <div className="mb-5 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]">{error}</div>}

        <div className="border border-border bg-white overflow-x-auto">
          {loading ? (
            <div className="p-8 overline">Loading...</div>
          ) : (
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="bg-accent/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-3 font-semibold">Slug</th>
                  <th className="text-left p-3 font-semibold">Name</th>
                  <th className="text-left p-3 font-semibold">External URL</th>
                  <th className="text-left p-3 font-semibold">Sections / Topics</th>
                  {canEdit && <th className="text-right p-3 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => (
                  <tr key={m.slug} className="border-t border-border">
                    <td className="p-3 font-mono text-[12.5px]">{m.slug}</td>
                    <td className="p-3 font-semibold">{m.name}</td>
                    <td className="p-3 text-muted-foreground truncate max-w-[260px]"><a className="link-underline" href={m.external_url} target="_blank" rel="noreferrer">{m.external_url}</a></td>
                    <td className="p-3 text-muted-foreground">{m.total_sections || "-"} / {m.total_topics || "-"}</td>
                    {canEdit && (
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2">
                          <button data-testid={`edit-module-${m.slug}`} onClick={() => startEdit(m)} className="h-9 px-3 inline-flex items-center gap-1.5 border border-border text-[12px] font-semibold hover:bg-accent">
                            <Edit2 className="h-3.5 w-3.5" /> Edit
                          </button>
                          <button data-testid={`delete-module-${m.slug}`} onClick={() => del(m)} className="h-9 px-3 inline-flex items-center gap-1.5 border border-destructive/30 text-destructive text-[12px] font-semibold hover:bg-destructive/5">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white border border-border max-w-lg w-full p-6">
            <div className="overline mb-2">{editing === "new" ? "New module" : "Edit module"}</div>
            <h2 className="font-display font-black text-xl tracking-tight mb-6">{editing === "new" ? "Add a training track" : form.name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Slug *</label>
                <input data-testid="module-slug-input" disabled={editing !== "new"} required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full h-11 px-3 border border-border bg-white disabled:bg-accent font-mono" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Name *</label>
                <input data-testid="module-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-11 px-3 border border-border bg-white" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Description *</label>
                <textarea required rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full p-3 border border-border bg-white text-[13.5px]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">External training URL *</label>
                <input required type="url" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  className="w-full h-11 px-3 border border-border bg-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Sections</label>
                  <input type="number" min="0" value={form.total_sections} onChange={(e) => setForm({ ...form, total_sections: e.target.value })}
                    className="w-full h-11 px-3 border border-border bg-white" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Topics</label>
                  <input type="number" min="0" value={form.total_topics} onChange={(e) => setForm({ ...form, total_topics: e.target.value })}
                    className="w-full h-11 px-3 border border-border bg-white" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={close} className="h-10 px-5 border border-border font-semibold text-[13px]">Cancel</button>
              <button data-testid="module-save-button" type="submit" className="h-10 px-5 bg-primary text-primary-foreground font-semibold text-[13px]">Save</button>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}
